import { SHBox, SHStack } from "@components/design-systems";
import { HamburgerSVG } from "@components/svgs/hamburger";
import { DELAY_TIME } from "@constants";
import { useSortable } from "@dnd-kit/sortable";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";
import { IconButton, Tooltip, tooltipClasses, useTheme } from "@mui/material";
import { memo, useMemo } from "react";
import {
  AnalysisSummaryRecommendationLabelConfigs,
  AnalysisSummaryStyleConfigs as StyleConfigs,
} from "../../../configs";
import {
  CurrentProductRecommendationStatusOptions,
  OtherProductRecommendationStatusOptions,
} from "../../../constants";
import { ProductRecommendationsLogo } from "../product-logo";
import { RecommendationSelect } from "../recommendation-select";

interface ProductRecommendationDragItemProps {
  index: number;
  recommendationData: Partial<ProductRecommendationDTO>;
  isReview?: boolean;
  disabled?: boolean;
}
export const ProductRecommendationDragItem = memo(
  ({
    index,
    recommendationData,
    isReview,
    disabled,
  }: ProductRecommendationDragItemProps) => {
    const { palette } = useTheme();

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: recommendationData.productId ?? "" });

    const renderBadge = () => {
      const { recommendationType, additionalCustomLabel } = recommendationData;

      const label = !recommendationType
        ? null
        : recommendationType === ProductRecommendationTypes.Custom
        ? additionalCustomLabel
        : recommendationType;

      const configs =
        recommendationType && label
          ? AnalysisSummaryRecommendationLabelConfigs[recommendationType]
          : null;
      const { backgroundColor } = configs || {};

      return (
        <SHStack
          sx={{
            borderStartStartRadius: "4px",
            borderEndStartRadius: "4px",
          }}
          bgcolor={backgroundColor}
          width={"10px"}
          height={50}
          alignItems={"center"}
          justifyContent={"center"}
        />
      );
    };

    const recommendationSelectOptions = useMemo(() => {
      return recommendationData?.isProductMain
        ? CurrentProductRecommendationStatusOptions
        : OtherProductRecommendationStatusOptions;
    }, [recommendationData?.isProductMain]);

    return (
      <SHStack
        ref={setNodeRef}
        {...attributes}
        direction={"row"}
        alignItems={"center"}
        gap={1}
        paddingY={0}
        bgcolor={palette.common.white}
        borderRadius={"4px"}
        height={50}
        minHeight={50}
        border={`1px solid ${palette.secondary[100]}`}
        zIndex={1}
        sx={{
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
            : undefined,
          transition,
          ...(isDragging && {
            zIndex: 2,
            borderBottom: `1px solid ${palette.primary.main}`,
            backgroundColor: palette.common.lightSteelBlue,
            filter: "drop-shadow(1px 1px 2px rgba(65, 73, 89, 0.25))",
            transition: "background-color 300ms ease, border-bottom 300ms ease",
          }),
        }}
      >
        <SHStack
          direction={"row"}
          height={"100%"}
          borderRight={`1px solid ${palette.secondary[100]}`}
          justifyContent={"space-between"}
        >
          {renderBadge()}

          <SHStack
            height={"100%"}
            direction={"row"}
            alignItems={"center"}
            paddingLeft={1}
            gap={1.5}
            width={StyleConfigs.PlatformRecommendationTitleColumnWidth - 15}
          >
            <SHStack
              width={30}
              height={"70%"}
              alignItems={"center"}
              justifyContent={"center"}
              sx={{
                borderRadius: "4px",
                cursor: "grab",
                ...(isDragging && {
                  cursor: "grabbing",
                }),
                ":hover": {
                  backgroundColor: palette.common.lighterGrey,
                },
              }}
              {...(disabled ? {} : listeners)}
            >
              <Tooltip
                enterDelay={DELAY_TIME}
                enterNextDelay={DELAY_TIME}
                title={"Move"}
                placement={"bottom"}
                slotProps={{
                  popper: {
                    sx: {
                      [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                        {
                          marginTop: "5px",
                        },
                    },
                  },
                }}
              >
                <SHBox component={"span"}>
                  <IconButton disabled sx={{ padding: 1 }}>
                    <HamburgerSVG
                      width={18}
                      height={18}
                      color={disabled ? palette.action.disabled : undefined}
                    />
                  </IconButton>
                </SHBox>
              </Tooltip>
            </SHStack>

            <ProductRecommendationsLogo
              disabled={disabled}
              recommendationData={recommendationData}
              isReview={isReview}
            />
          </SHStack>
        </SHStack>

        <RecommendationSelect
          itemIndex={index}
          disabled={disabled}
          recommendationData={recommendationData}
          selectOptions={recommendationSelectOptions}
        />
      </SHStack>
    );
  },
);
